import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, MenuItem, TextField, LinearProgress, Tooltip,
} from '@mui/material';
import { Insights, TrendingUp, TrendingDown, TrendingFlat, CheckCircle, Warning, Error as ErrIcon, Help } from '@mui/icons-material';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const YEAR = new Date().getFullYear();

const STATUS_CFG = {
  on_track:  { color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle sx={{ fontSize: 16, color: '#2e7d32' }} />, label: 'On Track' },
  at_risk:   { color: '#f57f17', bg: '#fffde7', icon: <Warning     sx={{ fontSize: 16, color: '#f57f17' }} />, label: 'At Risk'  },
  off_track: { color: '#b71c1c', bg: '#ffebee', icon: <ErrIcon     sx={{ fontSize: 16, color: '#b71c1c' }} />, label: 'Off Track' },
  no_data:   { color: '#9e9e9e', bg: '#f5f5f5', icon: <Help        sx={{ fontSize: 16, color: '#9e9e9e' }} />, label: 'No Data'  },
};

const CATEGORY_COLORS: Record<string, string> = {
  retention:   '#b71c1c',
  attendance:  '#e65100',
  recruitment: '#6a1b9a',
  learning:    '#00838f',
  performance: '#f57f17',
  wellbeing:   '#2e7d32',
  operations:  '#1565c0',
  workforce:   '#37474f',
};

const CATEGORY_LABELS: Record<string, string> = {
  retention: 'Retention', attendance: 'Attendance', recruitment: 'Recruitment',
  learning: 'Learning & Development', performance: 'Performance', wellbeing: 'Wellbeing',
  operations: 'HR Operations', workforce: 'Workforce',
};

interface KpiMetric {
  id: string; label: string; category: string;
  value: number | null; unit: string; target: number; targetLabel?: string;
  status: 'on_track' | 'at_risk' | 'off_track' | 'no_data';
  higherIsBetter: boolean; detail?: string;
}

function pctOfTarget(m: KpiMetric): number {
  if (m.value === null || m.target === 0) return 0;
  if (m.higherIsBetter) return Math.min(100, Math.round((m.value / m.target) * 100));
  return Math.min(100, Math.round((m.target / Math.max(m.value, 0.01)) * 100));
}

function formatValue(m: KpiMetric): string {
  if (m.value === null) return '—';
  const v = m.unit === '%' ? m.value.toFixed(1)
    : m.unit === '/5' ? m.value.toFixed(2)
    : m.unit === 'hrs' ? m.value.toFixed(1)
    : Math.round(m.value).toString();
  return `${v}${m.unit}`;
}

function KpiCard({ m }: { m: KpiMetric }) {
  const sc  = STATUS_CFG[m.status];
  const cc  = CATEGORY_COLORS[m.category] ?? '#546e7a';
  const pct = pctOfTarget(m);
  const barColor = m.status === 'on_track' ? '#2e7d32' : m.status === 'at_risk' ? '#f57f17' : m.status === 'off_track' ? '#b71c1c' : '#9e9e9e';

  return (
    <Paper sx={{ p: 2, height: '100%', borderTop: `3px solid ${cc}`, position: 'relative', overflow: 'hidden' }}>
      {/* Category label */}
      <Chip size="small" label={CATEGORY_LABELS[m.category] ?? m.category}
        sx={{ bgcolor: cc + '15', color: cc, fontSize: '0.6rem', mb: 1, height: 18 }} />

      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{m.label}</Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
        <Typography variant="h4" fontWeight={800} color={m.value === null ? '#9e9e9e' : cc}>
          {formatValue(m)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {sc.icon}
          <Typography variant="caption" color={sc.color}>{sc.label}</Typography>
        </Box>
      </Box>

      {/* Target */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Target: {m.targetLabel ?? `${m.higherIsBetter ? '≥' : '≤'} ${m.target}${m.unit}`}
      </Typography>

      {/* Progress toward target */}
      {m.value !== null && (
        <Tooltip title={`${pct}% of target`}>
          <LinearProgress variant="determinate" value={pct}
            sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: barColor } }} />
        </Tooltip>
      )}

      {m.detail && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          {m.detail}
        </Typography>
      )}
    </Paper>
  );
}

export default function HrMetrics() {
  const [year, setYear]       = useState(YEAR);
  const [data, setData]       = useState<{ metrics: KpiMetric[]; monthlyTrend: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/hr-metrics?year=${year}`);
      setData(d);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const metrics     = data?.metrics ?? [];
  const onTrack     = metrics.filter(m => m.status === 'on_track').length;
  const atRisk      = metrics.filter(m => m.status === 'at_risk').length;
  const offTrack    = metrics.filter(m => m.status === 'off_track').length;
  const noData      = metrics.filter(m => m.status === 'no_data').length;
  const overallPct  = metrics.filter(m => m.status !== 'no_data').length > 0
    ? Math.round((onTrack / metrics.filter(m => m.status !== 'no_data').length) * 100) : 0;

  // Group by category
  const grouped: Record<string, KpiMetric[]> = {};
  metrics.forEach(m => {
    if (!grouped[m.category]) grouped[m.category] = [];
    grouped[m.category].push(m);
  });

  const trend = data?.monthlyTrend ?? [];
  const maxTrendVal = Math.max(...trend.map((t: any) => Math.max(t.joiners, t.leavers, t.trainings)), 1);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1565c0', width: 48, height: 48 }}><Insights /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>HR Metrics & KPI Scorecard</Typography>
            <Typography variant="body2" color="text.secondary">
              HR effectiveness dashboard — turnover, absenteeism, hiring, training, wellbeing
            </Typography>
          </Box>
        </Box>
        <TextField select size="small" label="Year" value={year} onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 100 }}>
          {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Summary row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', border: `2px solid ${overallPct >= 80 ? '#2e7d32' : overallPct >= 60 ? '#f57f17' : '#b71c1c'}` }}>
                <Typography variant="h2" fontWeight={800}
                  color={overallPct >= 80 ? '#2e7d32' : overallPct >= 60 ? '#f57f17' : '#b71c1c'}>
                  {overallPct}%
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">KPIs On Track</Typography>
                <LinearProgress variant="determinate" value={overallPct}
                  sx={{ mt: 1, height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': { bgcolor: overallPct >= 80 ? '#2e7d32' : overallPct >= 60 ? '#f57f17' : '#b71c1c' } }} />
              </Paper>
            </Grid>
            {[
              { label: '✅ On Track',  value: onTrack,  color: '#2e7d32' },
              { label: '⚠️ At Risk',   value: atRisk,   color: '#f57f17' },
              { label: '❌ Off Track', value: offTrack, color: '#b71c1c' },
              { label: '— No Data',   value: noData,   color: '#9e9e9e' },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 3, md: 2.25 }} key={s.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {offTrack > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>{offTrack} metric{offTrack > 1 ? 's' : ''} off track</strong> — requires immediate HR attention.
            </Alert>
          )}

          {/* KPI cards by category */}
          {Object.entries(grouped).map(([cat, mets]) => (
            <Box key={cat} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{
                color: CATEGORY_COLORS[cat] ?? '#546e7a', mb: 1.5,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {CATEGORY_LABELS[cat] ?? cat}
              </Typography>
              <Grid container spacing={2}>
                {mets.map(m => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={m.id}>
                    <KpiCard m={m} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {/* Monthly trend table */}
          {trend.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Monthly Activity — {year}</Typography>
              <Paper sx={{ p: 2, overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 1, minWidth: 600 }}>
                  {/* Labels col */}
                  <Box sx={{ minWidth: 80 }}>
                    <Box sx={{ height: 24 }} />
                    {['Joiners', 'Leavers', 'Absences', 'Trainings'].map(l => (
                      <Typography key={l} variant="caption" color="text.secondary"
                        sx={{ display: 'block', height: 36, lineHeight: '36px' }}>{l}</Typography>
                    ))}
                  </Box>
                  {/* Month columns */}
                  {trend.map((t: any) => (
                    <Box key={t.month} sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5, fontSize: '0.6rem' }}>
                        {t.month.slice(5)}
                      </Typography>
                      {[
                        { val: t.joiners,   color: '#2e7d32' },
                        { val: t.leavers,   color: '#b71c1c' },
                        { val: t.absences,  color: '#e65100' },
                        { val: t.trainings, color: '#00838f' },
                      ].map(({ val, color }, i) => (
                        <Box key={i} sx={{ height: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', mb: 0 }}>
                          <Box sx={{
                            width: '70%', bgcolor: color,
                            height: val > 0 ? Math.max(4, Math.round((val / maxTrendVal) * 28)) : 2,
                            borderRadius: '2px 2px 0 0', opacity: val > 0 ? 0.85 : 0.2,
                            transition: 'height 0.3s',
                          }} />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: val > 0 ? color : 'text.disabled' }}>
                            {val || 0}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
                {/* Legend */}
                <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Joiners',   color: '#2e7d32' },
                    { label: 'Leavers',   color: '#b71c1c' },
                    { label: 'Absences',  color: '#e65100' },
                    { label: 'Trainings', color: '#00838f' },
                  ].map(s => (
                    <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, bgcolor: s.color, borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
